import { CommonModule } from '@angular/common';
import {
  afterRenderEffect,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  EmbeddedViewRef,
  inject,
  input,
  output,
  SecurityContext,
  signal,
  TemplateRef,
  untracked,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { AskEvaClient } from 'api-clients/ai-api-client';
import { NoteCompDataTypes } from 'src/app/workspace/shared/note-comp-data-types.enum';
import { WorkspaceService } from 'src/app/workspace/workspace.service';
import { EzPopoverTransitionDirective } from 'src/shared/directives/ez-popover-transition.directive';
import { EzPopoverDirective } from 'src/shared/directives/ez-popover.directive';
import { EzRouterLinkDirective } from 'src/shared/directives/ez-router-link.directive';
import { globalWindow } from 'src/shared/window-globals';
import { EvaSourcePopoverService } from 'src/app/eva-sidebar/ez-source-popover/eva-source-popover.service';
import { EzSourcePopoverComponent } from 'src/app/eva-sidebar/ez-source-popover/ez-source-popover.component';
import { EvaSourceLink, EvaSourceMetadata } from 'src/app/eva-sidebar/shared/models/eva-source-link.model';
import { EvaSourceName, EvaSourceType } from 'src/app/eva-sidebar/shared/models/eva-source.enum';

const placeholderClass = 'eva-source-placeholder';
const placeholderTextPrefix = 'EVA_LINK_';

@Component({
  selector: 'ez-eva-text-formatter',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    EzPopoverDirective,
    EzPopoverTransitionDirective,
    EzRouterLinkDirective,
    EzSourcePopoverComponent,
  ],
  templateUrl: './eva-text-formatter.component.html',
  styleUrl: './eva-text-formatter.component.scss',
})
export class EvaTextFormatterComponent {
  private static nextLinkId = 0;
  readonly response = input<string | null | undefined>('');
  readonly conversationId = input<number | null | undefined>();
  readonly #sanitizer = inject(DomSanitizer);
  readonly #vcr = inject(ViewContainerRef);
  readonly #askEvaClient = inject(AskEvaClient);
  readonly #sourcePopoverService = inject(EvaSourcePopoverService);
  readonly #workspaceService = inject(WorkspaceService);
  readonly #destroyRef = inject(DestroyRef);
  protected readonly contentHost = viewChild.required<ElementRef<HTMLElement>>('contentHost');
  protected readonly linkTemplate =
    viewChild.required<TemplateRef<{ $implicit: EvaSourceLink; linkId: string }>>('linkTemplate');
  protected readonly parsedResponse = signal<string>('');
  protected readonly rendered = output<void>();
  protected readonly openLinkId = signal<string | null>(null);
  protected readonly sourceMetadata = signal<Record<string, EvaSourceMetadata>>({});
  protected readonly loadingMessage = this.#sourcePopoverService.loadingMessage;
  #abortController?: AbortController;

  #sourceLinks: EvaSourceLink[] = [];
  #linkViews: EmbeddedViewRef<{ $implicit: EvaSourceLink; linkId: string }>[] = [];

  constructor() {
    this.#destroyRef.onDestroy(() => {
      this.#abortController?.abort();
      this.#destroyLinkViews();
    });

    effect(async () => {
      const text = this.response();
      this.#destroyLinkViews();
      if (!text) {
        this.#sourceLinks = [];
        this.parsedResponse.set('');
        return;
      }
      const { stripped, sourceLinks } = this.#extractSourceLinks(text);
      this.#sourceLinks = sourceLinks;
      const { marked } = await import('marked');
      marked.setOptions({ breaks: true }); // Enable single line breaks
      const rawHtml = this.#renderNewTabIcon(marked.parse(stripped) as string);
      const sanitized = this.#sanitizer.sanitize(SecurityContext.HTML, rawHtml) || '';
      this.parsedResponse.set(sanitized);
    });

    afterRenderEffect(() => {
      if (this.parsedResponse()) {
        // Untracked so signal reads during the embedded-view change detection in
        // #renderSourceLinks (the #linkTemplate reads openLinkId/sourceMetadata) don't become
        // dependencies of this effect. Otherwise a source-link click (which writes those signals
        // in onPopoverToggle) would re-run this effect, re-fire `rendered`, and scroll the last
        // answer to the bottom. This effect must depend only on parsedResponse().
        untracked(() => {
          this.#renderSourceLinks();
          this.rendered.emit();
        });
      }
    });
  }

  #renderNewTabIcon(html: string): string {
    const newTabLink = /<a(\b[^>]*href="https?:\/\/[^"]*"[^>]*)>([\s\S]*?)<\/a>/g;
    return html.replace(newTabLink, '<a$1>$2<span class="material-symbols-outlined external-link">open_in_new</span></a>');
  }

  protected async onPopoverToggle(event: ToggleEvent, link: EvaSourceLink, linkId: string): Promise<void> {
    this.openLinkId.set(event.newState === 'open' ? linkId : null);
    // Fetch on open, once per link. The popover is already open (native) — content fills in reactively as metadata resolves.
    if (event.newState !== 'open' || this.sourceMetadata()[linkId]) {
      return;
    }
    // Multi-click: cancel any in-flight metadata fetch from a prior rapid open so the source APIs aren't called repeatedly (latest wins).
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const metadata = await this.#sourcePopoverService.fetchSourceMetadata(link, signal);
    // Drop a superseded fetch: fetchSourceMetadata returns undefined on abort, and signal.aborted covers the case
    // where #resolveDiscussionApplicantName swallowed the abort and still returned a (now-stale) result.
    if (!metadata || signal.aborted) {
      return;
    }
    this.sourceMetadata.update(current => ({ ...current, [linkId]: metadata }));
  }

  protected onLinkClicked(popover: EzPopoverDirective, link: EvaSourceLink): void {
    // Discussion opens in the workspace side panel (the popover renders a button, not an anchor) — handle it here.
    if (link.type === EvaSourceType.Discussion && link.discussionId != null) {
      this.#openDiscussionInWorkspace(link.discussionId, link.applicantId);
      popover.hide();
      return;
    }
    // Other types: ez-source-popover (via EzRouterLinkDirective) handles navigation; here we keep the click metric and close.
    // Mirror EzRouterLinkDirective.resolvedUrl so the tracked URL matches the click target: absolute URLs pass through,
    // relative paths resolve through MrFive.
    const url = link.url.startsWith('http') ? link.url : globalWindow.UrlResolver.MrFive(link.url);
    this.#trackSourceLinkClick(url);
    popover.hide();
  }

  /** Opens an existing discussion in the workspace note panel (by id; the panel resolves its own applicant context). */
  #openDiscussionInWorkspace(discussionId: number, applicantId?: number): void {
    this.#workspaceService.workspaceState.openNote({ discussionId, type: NoteCompDataTypes.Select });
    // Discussion metric URL is tracked as-is (relative), unlike anchor links which track the resolved absolute URL.
    // This is a dummy url no actual Mr.Five page available.
    this.#trackSourceLinkClick(`applicant/${applicantId}/workspace/discussion/${discussionId}`);
  }

  /**
   * On click of any link that opens in a new tab: no popover to close, but the click must still be tracked.
   * ezRouterLink drives the new-tab navigation; here only the absolute urls needs to be sent.
   */
  protected triggerMetricsTracking(link: EvaSourceLink): void {
    this.#trackSourceLinkClick(link.url);
  }

  /** Edit email link is also tracked */
  #trackSourceLinkClick(url: string): void {
    const conversationId = this.conversationId();
    if (conversationId) {
      this.#askEvaClient.trackSourceLinkClick(conversationId, { url });
    }
  }

  #extractSourceLinks(text: string): { stripped: string; sourceLinks: EvaSourceLink[] } {
    const sourceLinks: EvaSourceLink[] = [];
    let stripped = text;

    stripped = stripped.replace(
      /<EvaSourceUri:PolicyUri:(\d+)>([^<]+)<\/EvaSourceUri:PolicyUri:\1>/g,
      (_match, policyId, sourceText) => {
        const index = sourceLinks.length;
        sourceLinks.push({
          url: globalWindow.UrlResolver.ApplicantPortal(`policy/${policyId}/summary/index`),
          text: sourceText,
          type: EvaSourceType.Policy,
          sourceName: EvaSourceName.Policy,
          policyId: +policyId,
        });
        return `<span class="${placeholderClass}">${placeholderTextPrefix}${index}</span>`;
      }
    );

    stripped = stripped.replace(
      /<EvaSourceUri:ApplicantUri:(\d+)>([^<]+)<\/EvaSourceUri:ApplicantUri:\1>/g,
      (_match, applicantId, sourceText) => {
        const index = sourceLinks.length;
        sourceLinks.push({
          url: `account/${applicantId}/overview`,
          text: sourceText,
          type: EvaSourceType.Applicant,
          sourceName: EvaSourceName.Applicant,
          applicantId: +applicantId,
        });
        return `<span class="${placeholderClass}">${placeholderTextPrefix}${index}</span>`;
      }
    );

    stripped = stripped.replace(
      /<EvaSourceUri:DiscussionUri:(\d+):(\d+)>([^<]+)<\/EvaSourceUri:DiscussionUri:\1:\2>/g,
      (_match, discussionId, applicantId, sourceText) => {
        const index = sourceLinks.length;
        sourceLinks.push({
          // Empty url: the popover renders the "Open Discussion" button (not an anchor) and onLinkClicked opens the workspace panel.
          url: '',
          text: sourceText,
          type: EvaSourceType.Discussion,
          sourceName: EvaSourceName.Discussion,
          discussionId: +discussionId,
          applicantId: +applicantId,
        });
        return `<span class="${placeholderClass}">${placeholderTextPrefix}${index}</span>`;
      }
    );

    stripped = stripped.replace(
      /<EvaEditEmailUri:([^>]+)>([^<]+)<\/EvaEditEmailUri>/g,
      (_match, url, sourceText) => {
        const index = sourceLinks.length;
        sourceLinks.push({
          url,
          text: sourceText,
          type: EvaSourceType.Email,
          sourceName: EvaSourceName.Email,
          openInNew: true,
        });
        return `<span class="${placeholderClass}">${placeholderTextPrefix}${index}</span>`;
      }
    );

    return { stripped, sourceLinks };
  }

  #renderSourceLinks(): void {
    const host = this.contentHost().nativeElement;
    const placeholders = host.querySelectorAll<HTMLElement>(`span.${placeholderClass}`);
    placeholders.forEach((placeholder: HTMLElement) => {
      const index = this.#parsePlaceholderIndex(placeholder.textContent);
      const link = index !== null ? this.#sourceLinks[index] : undefined;
      if (!link || !placeholder.parentNode) {
        return;
      }
      const linkId = `eva-source-link-${EvaTextFormatterComponent.nextLinkId++}`;
      const view = this.#vcr.createEmbeddedView(this.linkTemplate(), { $implicit: link, linkId });
      view.detectChanges();
      view.rootNodes.forEach(node => placeholder.parentNode?.insertBefore(node, placeholder));
      placeholder.remove();
      this.#linkViews.push(view);
    });
  }

  #parsePlaceholderIndex(text: string | null): number | null {
    if (!text) return null;
    const match = text.match(new RegExp(`^${placeholderTextPrefix}(\\d+)$`));
    return match ? parseInt(match[1], 10) : null;
  }

  #destroyLinkViews(): void {
    this.#linkViews.forEach(view => view.destroy());
    this.#linkViews = [];
  }

}
